// @ts-strict-ignore
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Dialogs } from 'analytics/events/dialog';

import { ErrorName } from 'errors';

import { observer } from 'mobx-react';

import { FieldError, FormProvider, useForm } from 'react-hook-form';

import { useStores } from 'mobx/hooks/useStores';

import { parseDateForInputField } from 'utils/DateUtils';

import { showToast } from 'utils/UserMessageUtils';

import PatientEpisode from 'models/PatientEpisode';
import { PatientEpisodeEndReason } from 'models/PatientEpisodeEndReason';

import { SelectOption } from 'models/SelectOption';

import StyledInput, { RHFStyledInput } from 'views/Widgets/StyledInput';

import { FormErrors } from 'components/Forms';
import { MessageDialog } from 'components/UIkit/atoms/Dialog';
import { FormSelect } from 'components/UIkit/atoms/Dropdown';
import { SelectActionMetaName } from 'components/UIkit/atoms/Dropdown/SelectUtils';
import { Modal } from 'components/UIkit/atoms/Modal';
import { FormModal } from 'components/UIkit/atoms/Modal/FormModal';

import './PatientEpisodeEndModal.scss';

const MAX_REASON_LENGTH = 100;

interface EpisodeEndReasonModalProps {
  isOpen: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  patientEpisode: PatientEpisode;
}

export interface IOptOutSubmitObject {
  reasonId: number;
  details: string;
  name: string;
}

interface EndEpisodeForm {
  endDate: string;
  reason: any; // TODO: replace with SelectOption<number> once issue fixed on RHF. see https://github.com/react-hook-form/react-hook-form/issues/9131
  newReason: string;
}

const usePatientEpisodeEndReasons = () => {
  const { patientEpisodesStore } = useStores();
  return patientEpisodesStore.episodeEndReasons.items.filter((reason) => reason.isSelectable);
};

export const PatientEpisodeEndModal = observer(
  ({ isOpen, onSubmit, onCancel, patientEpisode }: EpisodeEndReasonModalProps) => {
    const { patientEpisodesStore, patientPageStore, userStore } = useStores();
    const [details, setDetails] = useState('');
    const [saveNewCustomReason, setSaveNewCustomReason] = useState(true);
    const [removedReason, setRemovedReason] = useState<PatientEpisodeEndReason>(null);
    const [editedReason, setEditedReason] = useState<PatientEpisodeEndReason>(null);
    const [newReasonName, setNewReasonName] = useState('');

    const endEpisodeFormMethods = useForm<EndEpisodeForm>();
    const { setError, formState } = endEpisodeFormMethods;
    const { errors } = formState;

    const formDefaultValues: EndEpisodeForm = {
      endDate: parseDateForInputField(new Date()),
      reason: null,
      newReason: ''
    };

    const [selectedReasonOption, newReason] = endEpisodeFormMethods.watch(['reason', 'newReason']);
    const [isAddingNewReason, setIsAddingNewReason] = useState(false);
    const [isEndEpisodeLoading, setIsEndEpisodeLoading] = useState(false);
    const [isEditReasonLoading, setIsEditReasonLoading] = useState(false);
    const [isDeleteReasonLoading, setIsDeleteReasonLoading] = useState(false);
    const endReasons = usePatientEpisodeEndReasons();
    const endReasonOptions = useMemo(
      () =>
        endReasons.map((reason) => ({
          value: reason.id,
          label: reason.name,
          isEditable: Boolean(reason.institutionId) && userStore.isManager
        })),
      [endReasons, userStore.isManager]
    );

    const editReason = useCallback(
      (reasonOption: SelectOption<number>) => {
        setNewReasonName(reasonOption.label);
        const reason = endReasons.find(
          (reason: PatientEpisodeEndReason) => reason.id === reasonOption.value
        );
        setEditedReason(reason);
      },
      [endReasons]
    );

    const initState = useCallback(() => {
      setDetails('');
      setSaveNewCustomReason(true);
      patientEpisodesStore.populateEndReasons();
    }, [patientEpisodesStore]);

    useEffect(() => {
      if (isOpen) {
        initState();
      }
    }, [isOpen, initState]);

    const createReason = () => {
      return patientEpisodesStore.createPatientEpisodeEndReason({
        name: endEpisodeFormMethods.getValues('newReason'),
        isActive: saveNewCustomReason
      });
    };

    const handleSaveClicked = async (data: { endDate: string }) => {
      setIsEndEpisodeLoading(true);
      try {
        let reasonId;
        if (isAddingNewReason) {
          reasonId = await createReason();
        } else {
          reasonId = selectedReasonOption.value;
        }
        const { patient } = patientPageStore;
        await patientEpisodesStore.endEpisode(patientEpisode.patientId, patientEpisode.id, {
          reasonId,
          details,
          endDate: data.endDate
        });
        showToast({
          message: `Episode Ended for ${patient.fullName} - ${patientEpisodesStore.getEpisodeTitle(
            patientEpisode.id
          )}`
        });

        onSubmit();
        setIsAddingNewReason(false);
      } catch (error) {
        if (error.name === ErrorName.DuplicateEpisodeEndReason) {
          setError('newReason', {
            type: 'server',
            message: error?.ui?.title || 'Something went wrong'
          });
        } else {
          throw error;
        }
      } finally {
        setIsEndEpisodeLoading(false);
      }
    };

    const deleteReasonClicked = useCallback((reason: PatientEpisodeEndReason) => {
      setRemovedReason(reason);
    }, []);

    const handleReasonEdit = async () => {
      try {
        setIsEditReasonLoading(true);
        await patientEpisodesStore.updateEpisodeEndReason(editedReason.id, newReasonName);
        setEditedReason(null);
      } catch (error) {
        setIsEditReasonLoading(false);
        throw error;
      }
    };

    const sendRemoveReasonRequest = async () => {
      try {
        setIsDeleteReasonLoading(true);
        await patientEpisodesStore.deletePatientEpisodeEndReason(removedReason.id);
        endEpisodeFormMethods.setValue('reason', null);
        setEditedReason(null);
      } catch (error) {
        setIsDeleteReasonLoading(false);
        throw error;
      }
      closeRemoveReasonModal();
    };

    const onSaveNewReasonCheckboxClick = () => {
      setSaveNewCustomReason(!saveNewCustomReason);
    };

    const closeRemoveReasonModal = () => {
      setRemovedReason(null);
    };

    const renderRemoveReasonModal = () => {
      return (
        <MessageDialog
          id={Dialogs.RemoveReason}
          isOpen={removedReason !== null}
          title="Remove this reason from suggestions?"
          handleClose={closeRemoveReasonModal}
          primaryActionProps={{
            text: 'Remove',
            onClick: sendRemoveReasonRequest,
            disabled: isDeleteReasonLoading
          }}
          secondaryActionProps={{ text: 'Cancel', onClick: closeRemoveReasonModal }}
        >
          {removedReason ? `"${removedReason.name}"` : ''}
        </MessageDialog>
      );
    };

    const buildForm = () => {
      return (
        <div>
          <div className="form-top-row">
            <FormProvider {...endEpisodeFormMethods}>
              <FormSelect
                options={endReasonOptions}
                name="reason"
                placeholder="Select Reason"
                addText={userStore.isManager && 'Ending Reason'}
                onEditClick={editReason}
                onAddClick={() => setIsAddingNewReason(true)}
                onChange={(option, actionMeta) => {
                  if (option && actionMeta.action === SelectActionMetaName.Select) {
                    setIsAddingNewReason(false);
                  }
                }}
                label="End Reason"
                isDisabled={endReasonOptions.length === 0}
                isRequired={!newReason}
              />
            </FormProvider>
            <RHFStyledInput
              label="End Date"
              type="date"
              name="endDate"
              min={parseDateForInputField(patientEpisode?.startDate)}
              register={endEpisodeFormMethods.register}
              isRequired
              error={Boolean(endEpisodeFormMethods.formState.errors.endDate)}
            />
          </div>
          {isAddingNewReason && (
            <div className="mt-3">
              <RHFStyledInput
                isRequired
                register={endEpisodeFormMethods.register}
                maxLength={MAX_REASON_LENGTH}
                error={Boolean(endEpisodeFormMethods.formState.errors.newReason)}
                name="newReason"
                placeholder="Episode Ending Reason"
                rounded
              />
            </div>
          )}
          {(selectedReasonOption || isAddingNewReason) && (
            <div className="mt-3">
              <StyledInput
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={`Optional: add details to the episode ending reason. `}
                rounded
                type="textarea"
              />
            </div>
          )}
        </div>
      );
    };
    return (
      <div>
        {renderRemoveReasonModal()}

        {/*TODO replace to FormModal after use rhf with this form*/}
        <Modal
          resetDataAfterClose={() => {
            setIsEditReasonLoading(false);
            setIsDeleteReasonLoading(false);
          }}
          isOpen={editedReason !== null}
          title="Edit End Episode Reason"
          subtitle="Renaming an End Episode reason will update it for any previously ended episodes, as well."
          confirmActions={[
            {
              onClick: handleReasonEdit,
              text: isEditReasonLoading ? 'Saving...' : 'Save',
              disabled: isEditReasonLoading
            }
          ]}
          closeAction={{ onClick: () => setEditedReason(null), disabled: false }}
          secondaryAction={{
            type: 'button',
            onClick: () => deleteReasonClicked(editedReason),
            text: 'Delete',
            disabled: isEditReasonLoading
          }}
        >
          <div className="edit-reason-modal-content">
            <StyledInput
              rounded
              value={newReasonName}
              onChange={(e) => setNewReasonName(e.target.value)}
            />
          </div>
        </Modal>

        <FormModal
          defaultValues={formDefaultValues}
          methods={endEpisodeFormMethods}
          title="End Episode"
          isOpen={isOpen && editedReason === null}
          confirmActions={[
            {
              onClick: endEpisodeFormMethods.handleSubmit(handleSaveClicked),
              text: 'End Episode',
              disabled: isEndEpisodeLoading
            }
          ]}
          closeAction={{
            onClick: () => {
              setIsAddingNewReason(false);
              onCancel();
            },
            disabled: false
          }}
          secondaryAction={{
            type: 'controlled-labeled-checkbox',
            isVisible: isAddingNewReason,
            label: 'Save this reason for future use',
            id: 'save-reason',
            onChange: onSaveNewReasonCheckboxClick,
            checked: saveNewCustomReason
          }}
          resetDataAfterClose={() => setIsEndEpisodeLoading(false)}
        >
          <div className="end-episode-content">{buildForm()}</div>
          <FormErrors errors={errors as Record<string, FieldError>} />
        </FormModal>
      </div>
    );
  }
);
